import { useState } from "react";
import { observer } from "mobx-react-lite";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { Button } from "src/components/Button";
import { useApi } from "src/contexts/api.context";
import { useActiveParams } from "src/hooks/useActiveParams";
import { useActiveProjectQuery } from "src/hooks/useActiveProjectQuery";
import { useToaster } from "src/contexts/toaster.context";
import { useFlagIdentity } from "src/contexts/posthog.context";
import { useTranslation } from "src/translations";
import { ToastType } from "../Toast";
import { generateAndDownloadSummaryPdf } from "src/utils/generateAndDownloadSummaryPdf";
import type { World } from "src/types/World";
import type { MapState } from "src/types/MapState";

// Dev-fallback PDF export. Kept alongside the new paywalled ExportDesignButton
// (which renders via FLAGS.exportPaywall) so internal users can keep generating
// reports without going through the wizard. Both routes call into the same
// shared helper — this button just passes no options/metadata overrides.
export const SummaryPdfButton = observer(function SummaryPdfButton(props: {
  world: World | null;
  mapState: MapState;
}) {
  const { t } = useTranslation();
  const { projectId, scenarioId, designId } = useActiveParams();
  const api = useApi();
  const project = useActiveProjectQuery();
  const toaster = useToaster();
  const { capture } = useFlagIdentity();
  const [isLoading, setIsLoading] = useState(false);

  if (!props.world) return null;

  const onClick = async () => {
    if (isLoading) return;
    if (!projectId) {
      toaster.show({
        text: "Open a project before exporting a summary.",
        type: ToastType.WARNING,
        autoHide: true,
        id: crypto.randomUUID(),
      });
      return;
    }

    const world = props.world;
    if (!world) return;
    setIsLoading(true);
    try {
      const result = await generateAndDownloadSummaryPdf({
        world,
        projectId,
        projectName: project.data?.name ?? "Tessa project",
        api,
        mapState: props.mapState,
      });
      capture("summary_pdf_exported", {
        projectId,
        scenarioId,
        designId,
        hasCarbon: result.hasCarbon,
        zonesCount: result.zonesCount,
        buildingsCount: result.buildingsCount,
      });
    } catch (err) {
      console.error("summary pdf export failed:", err);
      toaster.show({
        text: "Couldn't generate the summary PDF. Please try again.",
        type: ToastType.DANGER,
        autoHide: true,
        id: crypto.randomUUID(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      title={t("toolbox.summaryPdf")}
      Icon={DocumentArrowDownIcon}
      onClick={onClick}
      isLoading={isLoading}
    />
  );
});
